import { Colors, Container, Icon, MultiSelectOutlined, type OptionProps } from '@amelio/ui-library';
import { useMemo } from 'react';

import type {
    OnSelectCompanyAttributeOption,
    SelectedFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';
import type { CompanyAttributeMap } from '@/utils/company';

import { useAmelioTranslations } from '@/hooks';
import { getAttributeEntriesWithSelectedValues } from '@/utils/filters';
import { decodeAndSanitize } from '@/utils/htmlSanitizer';
import { getLocalizedText } from '@/utils/localization';
import { getOptionLabelText } from '@/utils/optionLabel';

export interface DashboardFiltersProps {
    companyAttributes: OptionProps[];
    selectedCompanyAttributes: CompanyAttributeMap;
    filters: SelectedFilters;
    onSelectCompanyAttribute: (value: string) => void;
    onSelectCompanyAttributeOption: (params: OnSelectCompanyAttributeOption) => void;
}

export const DashboardFilters = ({
    companyAttributes,
    selectedCompanyAttributes,
    filters,
    onSelectCompanyAttribute,
    onSelectCompanyAttributeOption,
}: DashboardFiltersProps) => {
    const { t } = useAmelioTranslations();

    const selectedCompanyAttributeEntries = Object.entries(selectedCompanyAttributes ?? {});

    const attributeEntries = useMemo(
        () => getAttributeEntriesWithSelectedValues({ selectedCompanyAttributes, filters }),
        [selectedCompanyAttributes, filters],
    );

    return (
        <Container
            mt={'2xl'}
            direction={'row'}
            gap={'md'}
            className={'relative z-10 w-full flex-wrap'}
            data-testid={'dashboard-filters'}
        >
            <Container className={'shrink-0'}>
                <MultiSelectOutlined
                    label={'Essential Filter'}
                    placeholder={t('common.filter')}
                    icon={
                        <Icon
                            name={'essential-filter'}
                            color={Colors.BASE_400}
                        />
                    }
                    options={companyAttributes.map((attr) => ({
                        ...attr,
                        label: decodeAndSanitize(getOptionLabelText(attr.label)),
                    }))}
                    onChange={onSelectCompanyAttribute}
                    values={selectedCompanyAttributeEntries.map(([, attribute]) =>
                        decodeAndSanitize(attribute.name),
                    )}
                    sort={'none'}
                />
            </Container>

            {attributeEntries.map(({ attribute, selectedValues }) => (
                <Container
                    key={attribute.name}
                    className={'max-w-200 min-w-49.5 shrink-0'}
                >
                    <MultiSelectOutlined
                        options={(attribute.values ?? []).map((value) => ({
                            label: decodeAndSanitize(
                                'names' in value ? getLocalizedText(value.names) : value.name,
                            ),
                            value: value.id,
                        }))}
                        onChange={(value) => onSelectCompanyAttributeOption({ value, attribute })}
                        values={selectedValues}
                        showSelection={true}
                        label={decodeAndSanitize(attribute.name)}
                        sort={'asc'}
                    />
                </Container>
            ))}
        </Container>
    );
};
